<template>
    <prime-message
        v-if="duplicateMessageVisible"
        severity="warn"
        :closable="false"
        class="-mt-3 flex w-full"
        :pt="{
            wrapper: { class: 'flex-1' },
            text: { class: 'flex-1' }
        }"
        :pt-options="{
            mergeProps: true
        }"
    >
        <div
            class="flex flex-1 flex-col items-center gap-2 md:flex-row md:gap-4 lg:gap-8"
        >
            <div class="flex flex-1">
                {{ warningText }}
            </div>
            <div class="flex flex-none gap-4">
                <prime-button
                    icon="pi pi-pencil"
                    :label="editText"
                    text
                    severity="secondary"
                    @click="handleEditContract"
                />
                <prime-button
                    icon="pi pi-shopping-cart"
                    text
                    severity="secondary"
                    :label="$t('purchaseContract.viewCart')"
                    @click="setCartVisible(true)"
                />
            </div>
        </div>
    </prime-message>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const DuplicateContractCartFragment = graphql(`
    fragment DuplicateContractCartFragment on Cart {
        items {
            __typename
            ... on CartContractItem {
                contract {
                    __typename
                    ... on PlanContract {
                        id
                        planType
                        plan {
                            id
                        }
                    }
                }
            }
        }
    }
`)
</script>

<script setup lang="ts">
import { setCartVisible, useCartData } from '~/composables/use-cart'
import PrimeButton from 'primevue/button'
import PrimeMessage from 'primevue/message'
import { computed } from 'vue'
import { getFragmentData } from '~/resources/graphql'
import { useRouter } from 'vue-router'
import { useRouteLink } from '~/composables/routing'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useLayoutMode } from '~/composables/use-layout-mode'

const props = defineProps<{
    planId: string
    contractId: string | undefined
    warningText: string
    editText: string
}>()

const cart = useCartData()
const cartData = computed(() =>
    cart.value === undefined
        ? undefined
        : getFragmentData(DuplicateContractCartFragment, cart.value)
)

// stores id of first contract in cart that is also of this plan
const duplicateItem = computed(() => {
    let duplicateItem = null
    if (cartData.value === undefined) {
        return null
    }

    for (const item of cartData.value.items) {
        if (
            item.__typename === 'CartContractItem' &&
            item.contract.__typename === 'PlanContract'
        ) {
            // if the plans match
            if (item.contract.plan.id === props.planId) {
                // if the cart contract is not the current one we are working on
                if (item.contract.id !== props.contractId) {
                    duplicateItem = item.contract
                    break
                }
            }
        }
    }
    return duplicateItem
})

// Always show duplicate message on dev in kiosk mode so it alerts us to an issue
// But don't usually show in kiosk mode
const { isKiosk } = useLayoutMode()
const duplicateMessageVisible = computed(
    () =>
        duplicateItem.value !== null && (!isKiosk.value || import.meta.env.DEV)
)

const router = useRouter()
const activeFacility = useActiveFacility()
const { facilityEditContractLink } = useRouteLink()
function handleEditContract() {
    if (duplicateItem.value === null) {
        throw new Error()
    }

    router.push(
        facilityEditContractLink(
            activeFacility.value.slug,
            duplicateItem.value.planType,
            duplicateItem.value.id
        )
    )
}
</script>
